import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Edit, Trash2, Plus, Calendar } from 'lucide-react';
import { formatPhoneNumber } from '../utils/phoneFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageServers = () => {
  const navigate = useNavigate();
  const { token, getAuthHeaders, user } = useAuth();
  const [servers, setServers] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [serverAvailability, setServerAvailability] = useState({});
  const [serverBookings, setServerBookings] = useState({});
  const [serverAppointmentsByDate, setServerAppointmentsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingServer, setEditingServer] = useState(null);

  useEffect(() => {
    if (token) {
      fetchServers();
      fetchCalendars();
    }
  }, [token]);

  useEffect(() => {
    if (servers.length > 0) {
      fetchAllServerAvailability();
      fetchTodayBookings();
      fetchWeekAppointments();
    }
  }, [servers]);

  // Calculate 7-day window starting from today
  const getSevenDayWindow = () => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setHours(0, 0, 0, 0); // Set to start of day
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const sevenDayWindow = getSevenDayWindow();

  const fetchServers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/servers`, {
        headers: getAuthHeaders()
      });
      setServers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars`, {
        headers: getAuthHeaders()
      });
      setCalendars(response.data.data || []);
    } catch (err) {
      console.error('Error fetching calendars:', err);
    }
  };

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAllServerAvailability = async () => {
    try {
      const availabilityMap = {};
      
      // Fetch availability for all servers
      for (const server of servers) {
        const dateRangeParams = sevenDayWindow.map(d => formatDateLocal(d));
        const startDate = dateRangeParams[0];
        const endDate = dateRangeParams[6];
        
        try {
          const response = await axios.get(`${API_BASE_URL}/availability`, {
            headers: getAuthHeaders(),
            params: {
              server_id: server.id,
              start_date: startDate,
              end_date: endDate
            }
          });
          
          // Create a map of dates to availability status
          const dateMap = {};
          sevenDayWindow.forEach(date => {
            const dateStr = formatDateLocal(date);
            dateMap[dateStr] = false; // Default: not available
          });
          
          // Mark dates that have availability
          if (response.data.data && response.data.data.length > 0) {
            response.data.data.forEach(slot => {
              if (slot.is_available) {
                dateMap[slot.date] = true;
              }
            });
          }
          
          availabilityMap[server.id] = dateMap;
        } catch (err) {
          console.error(`Error fetching availability for server ${server.id}:`, err);
          // Set all dates as unavailable on error
          const dateMap = {};
          sevenDayWindow.forEach(date => {
            dateMap[formatDateLocal(date)] = false;
          });
          availabilityMap[server.id] = dateMap;
        }
      }
      
      setServerAvailability(availabilityMap);
    } catch (err) {
      console.error('Error fetching server availability:', err);
    }
  };

  const fetchTodayBookings = async () => {
    try {
      const today = formatDateLocal(new Date());
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      // Fetch all appointments for today (single API call instead of one per server)
      const response = await axios.get(`${API_BASE_URL}/appointments`, {
        headers: getAuthHeaders(),
        params: {
          start_date: today,
          end_date: today
        }
      });
      
      const appointments = response.data.data || [];
      
      // Count appointments per server - split into served (past) and remaining (future)
      const bookingsMap = {};
      servers.forEach(server => {
        const serverAppointments = appointments.filter(apt => apt.server_id === server.id);
        
        // Split appointments into served (past) and remaining (upcoming)
        const served = serverAppointments.filter(apt => {
          // Extract time from start_time (format: "YYYY-MM-DDTHH:MM:SS")
          const aptTime = apt.start_time?.split('T')[1]?.substring(0, 5) || '00:00';
          return aptTime < currentTime;
        }).length;
        
        const remaining = serverAppointments.length - served;
        
        bookingsMap[server.id] = {
          served: served,
          remaining: remaining,
          total: serverAppointments.length
        };
      });
      
      setServerBookings(bookingsMap);
    } catch (err) {
      console.error('Error fetching today\'s bookings:', err);
    }
  };

  const fetchWeekAppointments = async () => {
    try {
      const startDate = formatDateLocal(sevenDayWindow[0]);
      const endDate = formatDateLocal(sevenDayWindow[6]);
      
      // Fetch appointments for the 7-day window
      const response = await axios.get(`${API_BASE_URL}/appointments`, {
        headers: getAuthHeaders(),
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      const appointments = response.data.data || [];
      
      // Organize appointments by server and date
      const appointmentsByServerDate = {};
      servers.forEach(server => {
        appointmentsByServerDate[server.id] = {};
        sevenDayWindow.forEach(date => {
          const dateStr = formatDateLocal(date);
          appointmentsByServerDate[server.id][dateStr] = [];
        });
      });
      
      // Group appointments
      appointments.forEach(apt => {
        if (apt.server_id && appointmentsByServerDate[apt.server_id]) {
          const aptDate = apt.start_time.split('T')[0];
          if (appointmentsByServerDate[apt.server_id][aptDate]) {
            appointmentsByServerDate[apt.server_id][aptDate].push(apt);
          }
        }
      });
      
      setServerAppointmentsByDate(appointmentsByServerDate);
    } catch (err) {
      console.error('Error fetching week appointments:', err);
    }
  };

  const getCalendarNamesForServer = (serverId) => {
    const assignedCalendars = calendars.filter(cal => 
      (cal.server_ids || []).includes(serverId)
    );
    return assignedCalendars.length > 0 
      ? assignedCalendars.map(cal => cal.name).join(', ')
      : 'None';
  };

  const handleDelete = async (serverId, serverName) => {
    if (!window.confirm(`Are you sure you want to delete server "${serverName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/servers/${serverId}`, {
        headers: getAuthHeaders()
      });
      fetchServers();
    } catch (err) {
      console.error('Error deleting server:', err);
      alert('Failed to delete server: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <section className="flex-grow py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800">Manage Servers</h1>
            <button
              onClick={() => {
                setEditingServer(null);
                setShowAddModal(true);
              }}
              className="flex items-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Server
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="text-gray-600">Loading servers...</div>
            </div>
          ) : servers.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-600 text-lg mb-4">No servers found</p>
              <p className="text-gray-500 mb-6">Add your first server to get started</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors"
              >
                Add Server
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Calendars
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div>Available Dates</div>
                        <div className="text-xs font-normal mt-0.5">
                          {sevenDayWindow.map((date, idx) => {
                            const isToday = formatDateLocal(date) === formatDateLocal(new Date());
                            return (
                              <span 
                                key={idx} 
                                className={`inline-block w-8 text-center ${isToday ? 'text-red-500 font-semibold' : 'text-gray-400'}`}
                              >
                                {date.toLocaleDateString('en-US', { weekday: 'short' }).substring(0, 2)}
                              </span>
                            );
                          })}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div>Today&apos;s Bookings</div>
                        <div className="text-xs font-normal normal-case mt-0.5 text-gray-400">
                          <span className="text-green-600">Served</span> | <span className="text-orange-600">Remaining</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {servers.map((server) => (
                      <tr key={server.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {server.server_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {server.email || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {server.phone || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {getCalendarNamesForServer(server.id)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {sevenDayWindow.map((date, idx) => {
                              const dateStr = formatDateLocal(date);
                              const isAvailable = serverAvailability[server.id]?.[dateStr];
                              const appointments = serverAppointmentsByDate[server.id]?.[dateStr] || [];
                              const hasBookings = appointments.length > 0;
                              
                              // Determine if fully booked (check if server has availability slots and all are booked)
                              // For simplicity, we'll consider red if there are 8+ bookings (assuming full day)
                              const isFullyBooked = hasBookings && appointments.length >= 8;
                              
                              // Determine dot color
                              let dotColor = 'bg-gray-300'; // Not available
                              if (isAvailable) {
                                if (isFullyBooked) {
                                  dotColor = 'bg-red-500'; // Fully booked
                                } else if (hasBookings) {
                                  dotColor = 'bg-blue-500'; // Has bookings
                                } else {
                                  dotColor = 'bg-green-500'; // Available, no bookings
                                }
                              }
                              
                              // Build tooltip
                              let tooltip = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                              if (!isAvailable) {
                                tooltip += ': Not Working';
                              } else if (appointments.length === 0) {
                                tooltip += ': Available';
                              } else {
                                // Find next booking (earliest time)
                                const sortedApts = [...appointments].sort((a, b) => 
                                  a.start_time.localeCompare(b.start_time)
                                );
                                const nextApt = sortedApts[0];
                                const time = nextApt.start_time.split('T')[1]?.substring(0, 5);
                                tooltip += `\nNext: ${time}\nCustomer: ${nextApt.customer_name || 'Unknown'}\nService: ${nextApt.service_name || 'Unknown'}`;
                                if (appointments.length > 1) {
                                  tooltip += `\n+${appointments.length - 1} more booking(s)`;
                                }
                              }
                              
                              return (
                                <div
                                  key={idx}
                                  className="w-8 flex items-center justify-center"
                                  title={tooltip}
                                >
                                  <div className={`w-3 h-3 rounded-full ${dotColor}`} />
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-center">
                            <div className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                              <span className="text-green-700 font-semibold text-sm">
                                {serverBookings[server.id]?.served || 0}
                              </span>
                              <span className="mx-2 text-gray-400 font-light">|</span>
                              <span className="text-orange-600 font-semibold text-sm">
                                {serverBookings[server.id]?.remaining || 0}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            server.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {server.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => navigate(`/admin/servers/${server.id}/calendar`)}
                            className="text-green-600 hover:text-green-900 mr-4"
                            title="View Server Calendar"
                          >
                            <Calendar className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingServer(server);
                              setShowAddModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(server.id, server.server_name)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {showAddModal && (
        <ServerModal
          server={editingServer}
          onClose={() => {
            setShowAddModal(false);
            setEditingServer(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingServer(null);
            fetchServers();
          }}
          token={token}
        />
      )}
    </div>
  );
};

// Server Add/Edit Modal
const ServerModal = ({ server, onClose, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    server_name: server?.server_name || '',
    email: server?.email || '',
    phone: server?.phone || '',
    is_active: server?.is_active !== undefined ? server.is_active : true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (server) {
        // Update existing server
        await axios.put(
          `${API_BASE_URL}/servers/${server.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new server
        await axios.post(
          `${API_BASE_URL}/servers`,
          formData,
          { headers: getAuthHeaders() }
        );
      }
      onSuccess();
    } catch (err) {
      console.error('Error saving server:', err);
      setError(err.response?.data?.detail || 'Failed to save server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            {server ? 'Edit Server' : 'Add New Server'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Server Name *
            </label>
            <input
              type="text"
              value={formData.server_name}
              onChange={(e) => setFormData({ ...formData, server_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              required
              placeholder="Enter server name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              placeholder="server@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setFormData({ ...formData, phone: formatted });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              placeholder="123-456-7890"
              maxLength="12"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active Status
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-md transition-colors disabled:bg-yellow-300"
            >
              {loading ? 'Saving...' : server ? 'Update Server' : 'Create Server'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageServers;
